import React, { useEffect, useMemo, useState } from "react";
import { FileText, FolderKanban } from "lucide-react";

import {
  getAuth,
  onAuthStateChanged,
} from "firebase/auth";

import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";

import {
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

import { db, storage } from "../web/src/firebaseClient";

import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";

export default function MyResearchProjectsPage() {
  const auth = getAuth();

  const [activeTab, setActiveTab] = useState("applications");

  const [applicationStatus, setApplicationStatus] =
    useState("approved");

  const [applications, setApplications] = useState([]);
  const [projects, setProjects] = useState([]);

  const [loadingApplications, setLoadingApplications] =
    useState(true);

  const [loadingProjects, setLoadingProjects] =
    useState(true);

  const [submitting, setSubmitting] = useState(false);

  const [selectedApplicationId, setSelectedApplicationId] =
    useState("");

  const [projectTitle, setProjectTitle] = useState("");
  const [projectPurpose, setProjectPurpose] =
    useState("");

  const [projectSummary, setProjectSummary] =
    useState("");

  const [targetAudience, setTargetAudience] =
    useState("");

  const [method, setMethod] = useState("");

  const [result, setResult] = useState("");

  const [projectDocument, setProjectDocument] =
    useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        if (!currentUser) {
          setLoadingApplications(false);
          setLoadingProjects(false);
          return;
        }

        try {
          setLoadingApplications(true);
          setLoadingProjects(true);

          const applicationsQuery = query(
            collection(db, "researchProjectApplications"),
            where("uid", "==", currentUser.uid)
          );

          const applicationsSnapshot =
            await getDocs(applicationsQuery);

          const applicationsList =
            applicationsSnapshot.docs.map(
              (docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
              })
            );

          setApplications(applicationsList);

          const projectsQuery = query(
            collection(db, "researchProjects"),
            where("uid", "==", currentUser.uid)
          );

          const projectsSnapshot =
            await getDocs(projectsQuery);

          const projectsList =
            projectsSnapshot.docs.map(
              (docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
              })
            );

          setProjects(projectsList);
        } catch (error) {
          console.error(
            "Veriler alınamadı:",
            error
          );
        } finally {
          setLoadingApplications(false);
          setLoadingProjects(false);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  const filteredApplications = useMemo(() => {
    return applications.filter(
      (item) => item.status === applicationStatus
    );
  }, [applications, applicationStatus]);

  const handleSubmitProject = async (e) => {
    e.preventDefault();

    const currentUser = auth.currentUser;

    if (!currentUser) {
      alert("Giriş yapmalısınız.");
      return;
    }

    if (
      !projectTitle.trim() ||
      !projectPurpose.trim() ||
      !projectSummary.trim() ||
      !targetAudience.trim() ||
      !method.trim() ||
      !result.trim() ||
      !projectDocument
    ) {
      alert("Tüm alanlar zorunludur.");
      return;
    }

    if (projectDocument.type !== "application/pdf") {
      alert("Lütfen PDF yükleyin.");
      return;
    }

    try {
      setSubmitting(true);

      const selectedApplication = applications.find(
        (item) => item.id === selectedApplicationId
      );

      if (!selectedApplication) {
        alert("Başvuru bulunamadı.");
        return;
      }

      const existingProjectQuery = query(
        collection(db, "researchProjects"),
        where(
          "applicationId",
          "==",
          selectedApplication.id
        )
      );

      const existingProjectSnapshot =
        await getDocs(existingProjectQuery);

      if (!existingProjectSnapshot.empty) {
        alert(
          "Bu başvuru için zaten proje yüklenmiş."
        );

        setSubmitting(false);

        return;
      }

      const fileRef = ref(
        storage,
        `researchProjectApplications/${
          currentUser.uid
        }/${selectedApplication.id}/${Date.now()}-${
          projectDocument.name
        }`
      );

      await uploadBytes(fileRef, projectDocument);

      const documentUrl = await getDownloadURL(
        fileRef
      );

      await addDoc(
        collection(db, "researchProjects"),
        {
          applicationId: selectedApplication.id,

          title:
            selectedApplication.title ||
            selectedApplication.projectTitle ||
            "",

          institution:
            selectedApplication.institution || "",

          uid: currentUser.uid,

          projectTitle: projectTitle.trim(),

          projectPurpose:
            projectPurpose.trim(),

          projectSummary:
            projectSummary.trim(),

          targetAudience:
            targetAudience.trim(),

          method: method.trim(),

          result: result.trim(),

          projectDocumentUrl: documentUrl,

          projectDocumentName:
            projectDocument.name,

          status: "pending",

          likeCount: 0,

          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );

      alert(
        "Proje başarıyla gönderildi."
      );

      setSelectedApplicationId("");

      setProjectTitle("");
      setProjectPurpose("");
      setProjectSummary("");
      setTargetAudience("");
      setMethod("");
      setResult("");
      setProjectDocument(null);

      const refreshedProjects = await getDocs(
        query(
          collection(db, "researchProjects"),
          where("uid", "==", currentUser.uid)
        )
      );

      const refreshedList =
        refreshedProjects.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

      setProjects(refreshedList);
    } catch (error) {
      console.error(error);

      alert(
        "Proje gönderilirken hata oluştu."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-[#eef5ff]"
      style={{ fontFamily: "Poppins" }}
    >
      <InnovationHubHeader />

      <section className="px-4 py-6 md:px-6">
        <div className="mx-auto max-w-[1600px]">
          <div className="mb-6 rounded-2xl bg-white p-5 shadow-sm">
            <h1 className="text-2xl font-semibold text-[#173b7a]">
              Projelerim
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              Başvurularınızı ve projelerinizi
              yönetin
            </p>
          </div>

          <div className="mb-6 flex gap-3 rounded-2xl bg-[#eaf2ff] p-3 shadow-sm">
            <button
              type="button"
              onClick={() =>
                setActiveTab("applications")
              }
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                activeTab === "applications"
                  ? "bg-[#1565C0] text-white"
                  : "bg-white text-[#173b7a]"
              }`}
            >
              <FileText size={18} />
              Başvurularım
            </button>

            <button
              type="button"
              onClick={() =>
                setActiveTab("projects")
              }
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                activeTab === "projects"
                  ? "bg-[#1565C0] text-white"
                  : "bg-white text-[#173b7a]"
              }`}
            >
              <FolderKanban size={18} />
              Projelerim
            </button>
          </div>

          {activeTab === "applications" && (
            <section className="rounded-2xl bg-[#eaf2ff] p-5 shadow-sm">
              <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#173b7a]">
                    Başvurularım
                  </h2>

                  <p className="text-sm text-slate-500">
                    Onaylanan ve reddedilen
                    başvurular
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setApplicationStatus(
                        "approved"
                      )
                    }
                    className={`rounded-full px-4 py-2 text-sm font-medium ${
                      applicationStatus ===
                      "approved"
                        ? "bg-green-600 text-white"
                        : "bg-white text-green-700"
                    }`}
                  >
                    Onaylanan
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setApplicationStatus(
                        "rejected"
                      )
                    }
                    className={`rounded-full px-4 py-2 text-sm font-medium ${
                      applicationStatus ===
                      "rejected"
                        ? "bg-red-600 text-white"
                        : "bg-white text-red-700"
                    }`}
                  >
                    Reddedilen
                  </button>
                </div>
              </div>

              {loadingApplications ? (
                <p>Yükleniyor...</p>
              ) : filteredApplications.length ===
                0 ? (
                <div className="rounded-xl bg-white p-5 text-sm text-slate-500">
                  Başvuru bulunmuyor.
                </div>
              ) : (
                <div className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
                  {filteredApplications.map(
                    (item) => (
                      <ApplicationCard
                        key={item.id}
                        item={item}
                        hasProject={projects.some(
                          (project) =>
                            project.applicationId ===
                            item.id
                        )}
                        selectedApplicationId={
                          selectedApplicationId
                        }
                        setSelectedApplicationId={
                          setSelectedApplicationId
                        }
                        handleSubmitProject={
                          handleSubmitProject
                        }
                        projectTitle={
                          projectTitle
                        }
                        setProjectTitle={
                          setProjectTitle
                        }
                        projectPurpose={
                          projectPurpose
                        }
                        setProjectPurpose={
                          setProjectPurpose
                        }
                        projectSummary={
                          projectSummary
                        }
                        setProjectSummary={
                          setProjectSummary
                        }
                        targetAudience={
                          targetAudience
                        }
                        setTargetAudience={
                          setTargetAudience
                        }
                        method={method}
                        setMethod={setMethod}
                        result={result}
                        setResult={setResult}
                        projectDocument={
                          projectDocument
                        }
                        setProjectDocument={
                          setProjectDocument
                        }
                        submitting={submitting}
                      />
                    )
                  )}
                </div>
              )}
            </section>
          )}

          {activeTab === "projects" && (
            <section className="rounded-2xl bg-[#eaf2ff] p-5 shadow-sm">
              <h2 className="text-xl font-semibold text-[#173b7a]">
                Projelerim
              </h2>

              <p className="mb-5 text-sm text-slate-500">
                Size ait projeler
              </p>

              {loadingProjects ? (
                <p>Yükleniyor...</p>
              ) : projects.length === 0 ? (
                <div className="rounded-xl bg-white p-5 text-sm text-slate-500">
                  Henüz proje bulunmuyor.
                </div>
              ) : (
                <div className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
                  {projects.map((item) => (
                    <ProjectMiniCard
                      key={item.id}
                      item={item}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </section>

      <InnovationHubFooter />
    </main>
  );
}

function ApplicationCard({
  item,
  hasProject,
  selectedApplicationId,
  setSelectedApplicationId,
  handleSubmitProject,
  projectTitle,
  setProjectTitle,
  projectPurpose,
  setProjectPurpose,
  projectSummary,
  setProjectSummary,
  targetAudience,
  setTargetAudience,
  method,
  setMethod,
  result,
  setResult,
  projectDocument,
  setProjectDocument,
  submitting,
}) {
  const selectedForUpload =
    selectedApplicationId === item.id;

  const isFormValid =
    projectTitle.trim() &&
    projectPurpose.trim() &&
    projectSummary.trim() &&
    targetAudience.trim() &&
    method.trim() &&
    result.trim() &&
    projectDocument;

  return (
    <div className="rounded-2xl bg-white p-5 shadow-md">
      <div
        className={`mb-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
          item.status === "approved"
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-700"
        }`}
      >
        {item.status === "approved"
          ? "Onaylandı"
          : "Reddedildi"}
      </div>

      <h3 className="line-clamp-2 text-lg font-semibold text-[#173b7a]">
        {item.projectTitle ||
          "Başlıksız başvuru"}
      </h3>

      <p className="mt-2 line-clamp-4 text-sm text-slate-600">
        {item.projectSummary ||
          "Özet bilgisi bulunmuyor."}
      </p>

      {item.status === "approved" &&
        hasProject && (
          <div className="mt-5 inline-flex rounded-full bg-yellow-100 px-4 py-2 text-sm font-semibold text-yellow-700">
            Proje yüklendi
          </div>
        )}

      {item.status === "approved" &&
        !hasProject &&
        !selectedForUpload && (
          <button
            type="button"
            onClick={() =>
              setSelectedApplicationId(item.id)
            }
            className="mt-5 rounded-full bg-[#1565C0] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#0f57aa]"
          >
            Projeyi Yükle
          </button>
        )}

      {selectedForUpload && (
        <form
          onSubmit={handleSubmitProject}
          className="mt-5 space-y-4 border-t pt-5"
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Proje Başlığı *
            </label>

            <input
              type="text"
              value={projectTitle}
              onChange={(e) =>
                setProjectTitle(
                  e.target.value
                )
              }
              required
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Proje Amacı *
            </label>

            <textarea
              value={projectPurpose}
              onChange={(e) =>
                setProjectPurpose(
                  e.target.value
                )
              }
              required
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Proje Özeti *
            </label>

            <textarea
              value={projectSummary}
              onChange={(e) =>
                setProjectSummary(
                  e.target.value
                )
              }
              required
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Hedef Kitle *
            </label>

            <textarea
              value={targetAudience}
              onChange={(e) =>
                setTargetAudience(
                  e.target.value
                )
              }
              required
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Yöntem *
            </label>

            <textarea
              value={method}
              onChange={(e) =>
                setMethod(
                  e.target.value
                )
              }
              required
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Sonuç *
            </label>

            <textarea
              value={result}
              onChange={(e) =>
                setResult(
                  e.target.value
                )
              }
              required
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Proje Dokümanı PDF *
            </label>

            <input
              type="file"
              accept="application/pdf"
              required
              onChange={(e) =>
                setProjectDocument(
                  e.target.files?.[0] ||
                    null
                )
              }
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={
                submitting || !isFormValid
              }
              className="rounded-full bg-[#1565C0] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#0f57aa] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting
                ? "Gönderiliyor..."
                : "Gönder"}
            </button>

            <button
              type="button"
              onClick={() =>
                setSelectedApplicationId("")
              }
              className="rounded-full bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-200"
            >
              Vazgeç
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function ProjectMiniCard({ item }) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-md">
      <div className="mb-3 inline-flex rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-700">
        {item.status || "pending"}
      </div>

      <h3 className="line-clamp-2 text-lg font-semibold text-[#173b7a]">
        {item.projectTitle ||
          item.title ||
          "Başlıksız proje"}
      </h3>

      <p className="mt-2 line-clamp-4 text-sm text-slate-600">
        {item.projectSummary ||
          "Proje özeti bulunmuyor."}
      </p>

      {item.projectDocumentUrl && (
        <a
          href={item.projectDocumentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-block rounded-full bg-[#1565C0] px-4 py-2 text-sm font-medium text-white"
        >
          PDF Görüntüle
        </a>
      )}
    </div>
  );
}